import React from 'react';

export type ReceiptTemplateId =
  | 'modern_left_brand'
  | 'corporate_split'
  | 'supermarket_classic'
  | 'minimalist_mono'
  | 'thermal_banner'
  | 'auto_parts'
  | 'wholesale_ledger'
  | 'boutique_luxury'
  | 'express_counter'
  | 'cyber_tech';

export interface ReceiptTemplateMeta {
  id: ReceiptTemplateId;
  name: string;
  nameSi: string;
  description: string;
  icon: string;
  tag: string;
}

export const RECEIPT_TEMPLATES: ReceiptTemplateMeta[] = [
  {
    id: 'modern_left_brand',
    name: 'Modern Left-Logo Brand (Recommended)',
    nameSi: 'වම්පස ලාංඡනය සහිත ප්‍රධාන සැලසුම',
    description: 'Large store logo on the left, neatly aligned company name, address & phone on the right with bold thermal borders.',
    icon: '🏢',
    tag: 'DEFAULT & RECOMMENDED'
  },
  {
    id: 'corporate_split',
    name: 'Corporate Executive Split',
    nameSi: 'ආයතනික ද්විත්ව තීරු සැලසුම',
    description: 'Two-column invoice header with outlined tax & ticket metadata box, clean grid borders.',
    icon: '📊',
    tag: 'EXECUTIVE'
  },
  {
    id: 'supermarket_classic',
    name: 'Retail Supermarket Classic',
    nameSi: 'සුපිරි වෙළඳසැල් සාම්ප්‍රදායික',
    description: 'Centered prominent brand header, cashier details, dense itemized listing with bold savings total.',
    icon: '🛒',
    tag: 'RETAIL'
  },
  {
    id: 'minimalist_mono',
    name: 'Minimalist Mono (Ultra-Sharp)',
    nameSi: 'මොනොස්පේස් තියුණු සැලසුම',
    description: 'Heavy monospace typography with razor-sharp ASCII divider rules. Fast printing and paper-saving.',
    icon: '⚡',
    tag: 'ULTRA SHARP'
  },
  {
    id: 'thermal_banner',
    name: 'Inverted Thermal Banner',
    nameSi: 'තද කළු තාප බැනර් සැලසුම',
    description: 'Solid pitch-black header banner with reversed white text. Burns dark and ultra-striking on thermal paper.',
    icon: '🏷️',
    tag: 'HIGH CONTRAST'
  },
  {
    id: 'auto_parts',
    name: 'Auto Spares & Hardware Pro',
    nameSi: 'වාහන අමතර කොටස් හා දෘඩාංග',
    description: 'Engineered for auto spares & hardware with warranty periods, part numbers, and return policy disclaimer.',
    icon: '🔧',
    tag: 'AUTO & HARDWARE'
  },
  {
    id: 'wholesale_ledger',
    name: 'Wholesale & Credit Ledger',
    nameSi: 'තොග සහ ණය ගිණුම් සැලසුම',
    description: 'Prominent Customer CID card at top with credit terms, previous balance, and multi-payment breakdown.',
    icon: '📦',
    tag: 'WHOLESALE'
  },
  {
    id: 'boutique_luxury',
    name: 'Boutique & Luxury Frame',
    nameSi: 'සුඛෝපභෝගී රාමු සහිත සැලසුම',
    description: 'Double-bordered royal outer frame with wide-spaced typography and centered decorative flourishes.',
    icon: '✨',
    tag: 'LUXURY'
  },
  {
    id: 'express_counter',
    name: 'Express Counter & Token',
    nameSi: 'ක්ෂණික ටෝකන් කවුන්ටර සැලසුම',
    description: 'Massive order token number at the top for rapid counter pickup and kitchen/delivery packing.',
    icon: '🎫',
    tag: 'FAST PICKUP'
  },
  {
    id: 'cyber_tech',
    name: 'Cyber Clean Tech',
    nameSi: 'සයිබර් නවීන තාක්ෂණික',
    description: 'Geometric layout with pill badges for cashier and payment methods, dotted micro-dividers.',
    icon: '💎',
    tag: 'MODERN TECH'
  }
];

export interface ReceiptItem {
  product?: {
    name?: string;
    price?: number;
    emoji?: string;
  };
  name?: string;
  qty: number;
  price?: number;
  returnPolicy?: string;
  warrantyPeriod?: string;
  partNumber?: string;
}

export interface ReceiptCustomer {
  id?: number | string;
  name?: string;
  phone?: string;
  address?: string;
  points?: number;
  customFieldValue?: string;
}

export interface ReceiptPayment {
  method: string;
  amount: number;
  provider?: string;
  change?: number;
}

export interface ReceiptRendererData {
  id?: string;
  storeName?: string;
  storeLogo?: string | null;
  storeAddress?: string | null;
  storePhone?: string | null;
  time?: Date | string;
  cashierName?: string;
  customer?: ReceiptCustomer | null;
  cart?: ReceiptItem[];
  subtotal?: number;
  discountPct?: number;
  discountAmt?: number;
  storeCreditUsed?: number;
  total?: number;
  payments?: ReceiptPayment[];
  change?: number;
}

interface ReceiptTemplateRendererProps {
  id?: string;
  templateId?: ReceiptTemplateId | string;
  data: ReceiptRendererData;
  is4Inch?: boolean;
  showLogoOnBill?: boolean;
  hidePoweredByOnBill?: boolean;
  isCapture?: boolean;
  style?: React.CSSProperties;
  className?: string;
}

const formatRs = (num: number | undefined | null) => {
  const val = Number(num) || 0;
  return 'Rs. ' + val.toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatTime = (time: Date | string | undefined) => {
  if (!time) return { date: '', time: '' };
  const d = typeof time === 'string' ? new Date(time) : time;
  return {
    date: d.toLocaleDateString('en-LK'),
    time: d.toLocaleTimeString('en-LK', { hour: '2-digit', minute: '2-digit' })
  };
};

export const ReceiptTemplateRenderer: React.FC<ReceiptTemplateRendererProps> = ({
  id,
  templateId = 'modern_left_brand',
  data,
  is4Inch = true,
  showLogoOnBill = false,
  hidePoweredByOnBill = false,
  isCapture = false,
  style = {},
  className = ''
}) => {
  const activeTemplate: ReceiptTemplateId =
    RECEIPT_TEMPLATES.some(t => t.id === templateId) ? (templateId as ReceiptTemplateId) : 'modern_left_brand';

  const { date, time } = formatTime(data.time || new Date());
  const storeName = data.storeName || 'Store';
  const cart = data.cart || [];
  const itemCount = cart.reduce((sum, item) => sum + (Number(item.qty) || 0), 0);
  const targetWidth = isCapture ? (is4Inch ? '380px' : '280px') : '100%';

  // Base font family: Crisp, bold, thermal-optimized fonts that avoid thin hairline dropouts
  const baseFont =
    activeTemplate === 'minimalist_mono'
      ? "'SF Mono', Monaco, Menlo, Consolas, 'Courier New', monospace"
      : "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

  // Common container style
  const containerStyle: React.CSSProperties = {
    width: targetWidth,
    maxWidth: isCapture ? (is4Inch ? '380px' : '280px') : '100%',
    margin: isCapture ? '0' : '0 auto',
    padding: is4Inch ? '14px 16px' : '10px 10px',
    backgroundColor: '#ffffff',
    color: '#000000',
    fontFamily: baseFont,
    fontSize: is4Inch ? '11px' : '10px',
    lineHeight: 1.3,
    fontWeight: 700,
    boxSizing: 'border-box',
    WebkitFontSmoothing: 'none',
    textRendering: 'geometricPrecision',
    ...style
  };

  // Common items table renderer
  const renderItemsTable = (showWarranty = false, showPartNo = false) => (
    <div style={{ margin: '8px 0', borderBottom: '2px solid #000', paddingBottom: '4px' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 34px 62px 70px',
          fontWeight: 900,
          fontSize: is4Inch ? '11px' : '10px',
          borderBottom: '2px solid #000',
          paddingBottom: '3px',
          marginBottom: '4px',
          textTransform: 'uppercase'
        }}
      >
        <span>Item</span>
        <span style={{ textAlign: 'center' }}>Qty</span>
        <span style={{ textAlign: 'right' }}>Price</span>
        <span style={{ textAlign: 'right' }}>Amount</span>
      </div>

      {cart.map((item, idx) => {
        const itemName = item.product?.name || item.name || 'Item';
        const itemPrice = Number(item.product?.price ?? item.price ?? 0);
        const itemQty = Number(item.qty) || 1;
        const total = itemPrice * itemQty;
        const warranty = item.warrantyPeriod;
        const policy = item.returnPolicy;
        const partNo = item.partNumber;

        return (
          <div key={idx} style={{ marginBottom: '4px' }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 34px 62px 70px',
                fontSize: is4Inch ? '11px' : '10px',
                fontWeight: 700,
                alignItems: 'start'
              }}
            >
              <span
                style={{
                  fontWeight: 800,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: isCapture ? 'normal' : 'nowrap',
                  paddingRight: '4px'
                }}
              >
                {itemName}
              </span>
              <span style={{ textAlign: 'center', fontWeight: 800 }}>{itemQty}</span>
              <span style={{ textAlign: 'right', fontWeight: 700 }}>{formatRs(itemPrice)}</span>
              <span style={{ textAlign: 'right', fontWeight: 900 }}>{formatRs(total)}</span>
            </div>

            {/* Optional Part Number & Warranty details */}
            {(showPartNo && partNo) && (
              <div style={{ fontSize: '9px', fontWeight: 800, color: '#000', paddingLeft: '4px', marginTop: '1px' }}>
                Part #{partNo}
              </div>
            )}
            {(showWarranty && (warranty || policy)) && (
              <div style={{ fontSize: '9px', fontWeight: 700, color: '#000', fontStyle: 'italic', paddingLeft: '4px', marginTop: '1px' }}>
                {[warranty ? `Warranty: ${warranty}` : null, policy].filter(Boolean).join(' • ')}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  // Common totals renderer
  const renderTotals = (inverted = false) => {
    const subtotal = data.subtotal ?? data.total ?? 0;
    const discountPct = data.discountPct ?? 0;
    const discountAmt = data.discountAmt ?? 0;
    const storeCredit = data.storeCreditUsed ?? 0;
    const grandTotal = data.total ?? subtotal;

    return (
      <div style={{ margin: '6px 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px', fontWeight: 700 }}>
          <span>Subtotal</span>
          <span style={{ fontWeight: 800 }}>{formatRs(subtotal)}</span>
        </div>
        {discountPct > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px', fontWeight: 700 }}>
            <span>Discount ({discountPct}%)</span>
            <span style={{ fontWeight: 800 }}>- {formatRs(discountAmt)}</span>
          </div>
        )}
        {storeCredit > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px', fontWeight: 700 }}>
            <span>Store Credit</span>
            <span style={{ fontWeight: 800 }}>- {formatRs(storeCredit)}</span>
          </div>
        )}

        {/* Total Highlight Box */}
        <div
          style={{
            marginTop: '4px',
            padding: inverted ? '6px 8px' : '4px 0',
            backgroundColor: inverted ? '#000000' : 'transparent',
            color: inverted ? '#ffffff' : '#000000',
            borderTop: inverted ? 'none' : '2px solid #000000',
            borderBottom: inverted ? 'none' : '2px solid #000000',
            borderRadius: inverted ? '4px' : '0px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <span style={{ fontSize: is4Inch ? '14px' : '12px', fontWeight: 900, letterSpacing: '0.5px' }}>
            TOTAL
          </span>
          <span style={{ fontSize: is4Inch ? '16px' : '14px', fontWeight: 900 }}>
            {formatRs(grandTotal)}
          </span>
        </div>
      </div>
    );
  };

  // Common payment & change renderer
  const renderPayments = () => {
    const payments = data.payments || [];
    return (
      <div style={{ borderTop: '1px dashed #000', paddingTop: '4px', marginTop: '6px', fontSize: is4Inch ? '10.5px' : '9.5px' }}>
        {payments.map((p, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1px' }}>
            <span style={{ textTransform: 'uppercase', fontWeight: 700 }}>
              Paid ({p.method.replace(/_/g, ' ')}{p.provider ? ` - ${p.provider}` : ''})
            </span>
            <span style={{ fontWeight: 800 }}>{formatRs(p.amount)}</span>
          </div>
        ))}
        {data.change !== undefined && Number(data.change) > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2px', fontWeight: 900 }}>
            <span>CHANGE</span>
            <span>{formatRs(data.change)}</span>
          </div>
        )}
      </div>
    );
  };

  // Common footer
  const renderFooter = (customNote?: string) => (
    <div style={{ textAlign: 'center', borderTop: '1px dashed #000', paddingTop: '6px', marginTop: '8px' }}>
      <div style={{ fontSize: is4Inch ? '10px' : '9px', fontWeight: 900 }}>
        {customNote || `Thank you for choosing ${storeName}!`}
      </div>
      <div style={{ fontSize: is4Inch ? '9.5px' : '8.5px', fontWeight: 700, marginTop: '2px' }}>
        ඔබට ස්තුතියි! නැවත පැමිණෙන්න. 🙏
      </div>
      {showLogoOnBill && (
        <img
          crossOrigin="anonymous"
          src="/kangavena-logo.png"
          alt="Kangavena"
          style={{ height: 16, objectFit: 'contain', margin: '6px auto 2px', display: 'block' }}
        />
      )}
      {!hidePoweredByOnBill && (
        <div style={{ fontSize: '7.5px', fontWeight: 600, color: '#000', marginTop: '4px', letterSpacing: '0.5px' }}>
          Powered by Kangavena POS Cloud • {date}
        </div>
      )}
    </div>
  );

  // ══════════════════════════════════════════════════════════════════
  // TEMPLATE 1: MODERN LEFT-LOGO BRAND (USER'S EXPLICIT REQUEST)
  // ══════════════════════════════════════════════════════════════════
  if (activeTemplate === 'modern_left_brand') {
    return (
      <div id={id} className={`kangavena-receipt-card ${className}`} style={containerStyle}>
        {/* Split Header: Big Logo on Left, Details Aligned on Right */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            borderBottom: '3px double #000',
            paddingBottom: '8px',
            marginBottom: '6px'
          }}
        >
          {data.storeLogo ? (
            <div style={{ flexShrink: 0, width: is4Inch ? '72px' : '56px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img
                crossOrigin="anonymous"
                src={data.storeLogo}
                alt=""
                style={{
                  width: is4Inch ? '70px' : '54px',
                  maxHeight: is4Inch ? '70px' : '54px',
                  objectFit: 'contain',
                  display: 'block'
                }}
              />
            </div>
          ) : (
            <div style={{ flexShrink: 0, width: is4Inch ? '48px' : '36px', height: is4Inch ? '48px' : '36px', border: '2px solid #000', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', fontWeight: 900 }}>
              🏢
            </div>
          )}

          {/* Right-aligned Company Details */}
          <div style={{ flex: 1, textAlign: 'right', minWidth: 0 }}>
            <div
              style={{
                fontSize: is4Inch ? '16px' : '13px',
                fontWeight: 900,
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                lineHeight: 1.15,
                color: '#000'
              }}
            >
              {storeName}
            </div>
            <div style={{ fontSize: '8.5px', fontWeight: 700, color: '#000', marginTop: '1px' }}>
              Luna AI POS System
            </div>
            {data.storeAddress && (
              <div style={{ fontSize: is4Inch ? '9px' : '8px', fontWeight: 700, color: '#000', marginTop: '2px', lineHeight: 1.2 }}>
                {data.storeAddress}
              </div>
            )}
            {data.storePhone && (
              <div style={{ fontSize: is4Inch ? '9.5px' : '8.5px', fontWeight: 800, color: '#000', marginTop: '1px' }}>
                Tel: {data.storePhone}
              </div>
            )}
          </div>
        </div>

        {/* Transaction Meta Bar */}
        <div style={{ borderBottom: '1px dashed #000', paddingBottom: '4px', marginBottom: '6px', fontSize: is4Inch ? '10px' : '9px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1px' }}>
            <span>Ticket: <strong style={{ fontWeight: 900 }}>{data.id || 'TX-PENDING'}</strong></span>
            <span>Date: <strong>{date} {time}</strong></span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Cashier: <strong>{data.cashierName || 'Manager'}</strong></span>
            {data.customer?.name && (
              <span>Customer: <strong>{data.customer.name}</strong></span>
            )}
          </div>
        </div>

        {/* Items Table */}
        {renderItemsTable(true)}

        {/* Totals & Payments */}
        {renderTotals()}
        {renderPayments()}

        {/* Sold items counter */}
        <div style={{ textAlign: 'center', borderTop: '1px dashed #000', paddingTop: '4px', marginTop: '6px', fontSize: '9.5px', fontWeight: 800 }}>
          Sold Item Count = {itemCount}
        </div>

        {/* Footer */}
        {renderFooter()}
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════
  // TEMPLATE 2: CORPORATE EXECUTIVE SPLIT
  // ══════════════════════════════════════════════════════════════════
  if (activeTemplate === 'corporate_split') {
    return (
      <div id={id} className={`kangavena-receipt-card ${className}`} style={containerStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '2px solid #000', paddingBottom: '6px', marginBottom: '6px' }}>
          <div>
            {data.storeLogo && (
              <img crossOrigin="anonymous" src={data.storeLogo} alt="" style={{ height: '36px', maxHeight: '36px', objectFit: 'contain', marginBottom: '2px' }} />
            )}
            <div style={{ fontSize: is4Inch ? '15px' : '13px', fontWeight: 900, textTransform: 'uppercase' }}>{storeName}</div>
            <div style={{ fontSize: '8px', fontWeight: 800 }}>COMMERCIAL TAX INVOICE</div>
            {data.storePhone && <div style={{ fontSize: '8.5px', fontWeight: 700 }}>Hotline: {data.storePhone}</div>}
          </div>
          <div style={{ border: '1.5px solid #000', padding: '4px 6px', borderRadius: '4px', textAlign: 'right', fontSize: '8.5px' }}>
            <div><strong>INV:</strong> {data.id || 'N/A'}</div>
            <div><strong>DATE:</strong> {date}</div>
            <div><strong>TIME:</strong> {time}</div>
          </div>
        </div>
        {data.storeAddress && (
          <div style={{ fontSize: '8.5px', fontWeight: 700, marginBottom: '6px' }}>{data.storeAddress}</div>
        )}
        {renderItemsTable(false)}
        {renderTotals()}
        {renderPayments()}
        {renderFooter()}
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════
  // TEMPLATE 3: RETAIL SUPERMARKET CLASSIC
  // ══════════════════════════════════════════════════════════════════
  if (activeTemplate === 'supermarket_classic') {
    return (
      <div id={id} className={`kangavena-receipt-card ${className}`} style={containerStyle}>
        <div style={{ textAlign: 'center', borderBottom: '1px dashed #000', paddingBottom: '6px', marginBottom: '6px' }}>
          {data.storeLogo && (
            <img crossOrigin="anonymous" src={data.storeLogo} alt="" style={{ height: '40px', objectFit: 'contain', margin: '0 auto 4px' }} />
          )}
          <div style={{ fontSize: is4Inch ? '17px' : '14px', fontWeight: 900, textTransform: 'uppercase' }}>{storeName}</div>
          {data.storeAddress && <div style={{ fontSize: '9px', fontWeight: 700 }}>{data.storeAddress}</div>}
          {data.storePhone && <div style={{ fontSize: '9px', fontWeight: 800 }}>Tel: {data.storePhone}</div>}
          <div style={{ fontSize: '8px', fontWeight: 800, marginTop: '2px' }}>*** CASH RECEIPT ***</div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', borderBottom: '1px dashed #000', paddingBottom: '3px', marginBottom: '4px' }}>
          <span>BILL: {data.id || '0000'}</span>
          <span>CASHIER: {data.cashierName || 'POS'}</span>
          <span>{date}</span>
        </div>
        {renderItemsTable(false)}
        {renderTotals()}
        {renderPayments()}
        <div style={{ textAlign: 'center', borderTop: '1px dashed #000', paddingTop: '4px', marginTop: '6px', fontSize: '9px', fontWeight: 800 }}>
          Items: {itemCount} • Thank You!
        </div>
        {renderFooter()}
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════
  // TEMPLATE 4: MINIMALIST MONO (ULTRA-SHARP)
  // ══════════════════════════════════════════════════════════════════
  if (activeTemplate === 'minimalist_mono') {
    return (
      <div id={id} className={`kangavena-receipt-card ${className}`} style={containerStyle}>
        <div style={{ textAlign: 'center', marginBottom: '4px' }}>
          <div style={{ fontSize: is4Inch ? '16px' : '13px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '-0.5px' }}>
            {storeName}
          </div>
          {data.storeAddress && <div style={{ fontSize: '8.5px', fontWeight: 700 }}>{data.storeAddress}</div>}
          {data.storePhone && <div style={{ fontSize: '8.5px', fontWeight: 700 }}>TEL: {data.storePhone}</div>}
          <div style={{ fontSize: '8px', letterSpacing: '1px', marginTop: '2px' }}>================================</div>
        </div>
        <div style={{ fontSize: '9px', marginBottom: '4px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>TX: {data.id || 'N/A'}</span>
            <span>{date} {time}</span>
          </div>
          <div>CASHIER: {data.cashierName || 'ADMIN'}</div>
        </div>
        <div style={{ fontSize: '8px', letterSpacing: '1px' }}>--------------------------------</div>
        {renderItemsTable(false)}
        {renderTotals()}
        {renderPayments()}
        <div style={{ fontSize: '8px', letterSpacing: '1px', textAlign: 'center', marginTop: '4px' }}>================================</div>
        {renderFooter()}
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════
  // TEMPLATE 5: INVERTED THERMAL BANNER
  // ══════════════════════════════════════════════════════════════════
  if (activeTemplate === 'thermal_banner') {
    return (
      <div id={id} className={`kangavena-receipt-card ${className}`} style={containerStyle}>
        <div style={{ backgroundColor: '#000000', color: '#ffffff', padding: '8px 10px', borderRadius: '4px', textAlign: 'center', marginBottom: '8px' }}>
          {data.storeLogo && (
            <img crossOrigin="anonymous" src={data.storeLogo} alt="" style={{ height: '32px', filter: 'brightness(0) invert(1)', objectFit: 'contain', margin: '0 auto 2px' }} />
          )}
          <div style={{ fontSize: is4Inch ? '16px' : '14px', fontWeight: 900, letterSpacing: '1px', textTransform: 'uppercase' }}>
            {storeName}
          </div>
          {data.storePhone && <div style={{ fontSize: '8.5px', fontWeight: 700 }}>TEL: {data.storePhone}</div>}
        </div>
        {data.storeAddress && (
          <div style={{ fontSize: '9px', textAlign: 'center', fontWeight: 700, marginBottom: '6px' }}>{data.storeAddress}</div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9.5px', borderBottom: '2px solid #000', paddingBottom: '3px', marginBottom: '4px' }}>
          <span>Ticket: <strong>{data.id || 'N/A'}</strong></span>
          <span>{date} {time}</span>
        </div>
        {renderItemsTable(false)}
        {renderTotals(true)}
        {renderPayments()}
        {renderFooter()}
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════
  // TEMPLATE 6: AUTO SPARES & HARDWARE PRO
  // ══════════════════════════════════════════════════════════════════
  if (activeTemplate === 'auto_parts') {
    return (
      <div id={id} className={`kangavena-receipt-card ${className}`} style={containerStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '3px solid #000', paddingBottom: '6px', marginBottom: '6px' }}>
          {data.storeLogo ? (
            <img crossOrigin="anonymous" src={data.storeLogo} alt="" style={{ width: '60px', maxHeight: '54px', objectFit: 'contain' }} />
          ) : (
            <div style={{ fontSize: '24px' }}>🚗</div>
          )}
          <div style={{ textAlign: 'right', flex: 1, paddingLeft: '8px' }}>
            <div style={{ fontSize: is4Inch ? '16px' : '13px', fontWeight: 900, textTransform: 'uppercase' }}>{storeName}</div>
            <div style={{ fontSize: '8.5px', fontWeight: 900 }}>GENUINE AUTOMOTIVE SPARE PARTS</div>
            {data.storeAddress && <div style={{ fontSize: '8.5px', fontWeight: 700 }}>{data.storeAddress}</div>}
            {data.storePhone && <div style={{ fontSize: '9px', fontWeight: 800 }}>Hotline: {data.storePhone}</div>}
          </div>
        </div>

        <div style={{ border: '1px solid #000', padding: '4px 6px', borderRadius: '4px', marginBottom: '6px', fontSize: '9.5px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Ticket: <strong>{data.id || 'TX-PARTS'}</strong></span>
            <span>Date: <strong>{date} {time}</strong></span>
          </div>
          {data.customer && (
            <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px dashed #000', paddingTop: '2px', marginTop: '2px' }}>
              <span>Vehicle/Customer: <strong>{data.customer.name}</strong></span>
              {data.customer.phone && <span>Tel: {data.customer.phone}</span>}
            </div>
          )}
        </div>

        {renderItemsTable(true, true)}
        {renderTotals()}
        {renderPayments()}

        {/* Auto Parts specific warranty notice */}
        <div style={{ border: '1px solid #000', padding: '4px 6px', borderRadius: '4px', marginTop: '6px', fontSize: '8px', lineHeight: 1.25 }}>
          <strong>Warranty Policy:</strong> Electrical parts & sensors carry no return once fitted. Physical receipt required for all warranty claims.
        </div>
        {renderFooter()}
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════
  // TEMPLATE 7: WHOLESALE & CREDIT LEDGER
  // ══════════════════════════════════════════════════════════════════
  if (activeTemplate === 'wholesale_ledger') {
    return (
      <div id={id} className={`kangavena-receipt-card ${className}`} style={containerStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '2px solid #000', paddingBottom: '6px', marginBottom: '6px' }}>
          <div>
            <div style={{ fontSize: is4Inch ? '16px' : '14px', fontWeight: 900, textTransform: 'uppercase' }}>{storeName}</div>
            <div style={{ fontSize: '8.5px', fontWeight: 800 }}>WHOLESALE DISTRIBUTORS</div>
            {data.storePhone && <div style={{ fontSize: '8.5px', fontWeight: 700 }}>Tel: {data.storePhone}</div>}
          </div>
          <div style={{ textAlign: 'right', fontSize: '8.5px' }}>
            <div><strong>TICKET:</strong> {data.id}</div>
            <div><strong>DATE:</strong> {date}</div>
          </div>
        </div>

        {/* Dedicated Wholesale Customer Card */}
        <div style={{ border: '1.5px solid #000', padding: '6px', borderRadius: '4px', marginBottom: '6px', fontSize: '9px' }}>
          <div style={{ fontWeight: 900, textTransform: 'uppercase', borderBottom: '1px solid #000', paddingBottom: '2px', marginBottom: '2px' }}>
            Customer Ledger Account
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Name: <strong>{data.customer?.name || 'Cash Customer'}</strong></span>
            {data.customer?.id && <span>CID: <strong>CID-{String(data.customer.id).padStart(4, '0')}</strong></span>}
          </div>
          {data.customer?.phone && <div>Tel: {data.customer.phone}</div>}
          {data.customer?.address && <div>Addr: {data.customer.address}</div>}
        </div>

        {renderItemsTable(false)}
        {renderTotals()}
        {renderPayments()}
        {renderFooter()}
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════
  // TEMPLATE 8: BOUTIQUE & LUXURY FRAME
  // ══════════════════════════════════════════════════════════════════
  if (activeTemplate === 'boutique_luxury') {
    return (
      <div
        id={id}
        className={`kangavena-receipt-card ${className}`}
        style={{
          ...containerStyle,
          border: '3px double #000000',
          padding: is4Inch ? '14px' : '10px'
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '8px' }}>
          {data.storeLogo && (
            <img crossOrigin="anonymous" src={data.storeLogo} alt="" style={{ height: '36px', objectFit: 'contain', margin: '0 auto 4px' }} />
          )}
          <div style={{ fontSize: is4Inch ? '16px' : '13px', fontWeight: 900, letterSpacing: '2px', textTransform: 'uppercase' }}>
            {storeName}
          </div>
          <div style={{ fontSize: '8px', letterSpacing: '1px', marginTop: '1px' }}>✦ OFFICIAL RECEIPT ✦</div>
          {data.storeAddress && <div style={{ fontSize: '8.5px', fontWeight: 700, marginTop: '2px' }}>{data.storeAddress}</div>}
          {data.storePhone && <div style={{ fontSize: '8.5px', fontWeight: 700 }}>{data.storePhone}</div>}
          <div style={{ fontSize: '8px', margin: '4px 0' }}>♦ ♦ ♦</div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', borderBottom: '1px solid #000', paddingBottom: '3px', marginBottom: '4px' }}>
          <span>NO: {data.id}</span>
          <span>{date}</span>
        </div>

        {renderItemsTable(false)}
        {renderTotals()}
        {renderPayments()}
        <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '8px' }}>♦ ♦ ♦</div>
        {renderFooter('Thank you for your patronage')}
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════
  // TEMPLATE 9: EXPRESS COUNTER & TOKEN
  // ══════════════════════════════════════════════════════════════════
  if (activeTemplate === 'express_counter') {
    const tokenNo = (data.id || '101').replace(/\D/g, '').slice(-3) || '101';
    return (
      <div id={id} className={`kangavena-receipt-card ${className}`} style={containerStyle}>
        {/* Giant Pickup Token */}
        <div style={{ border: '3px solid #000', borderRadius: '6px', textAlign: 'center', padding: '6px', marginBottom: '6px' }}>
          <div style={{ fontSize: '10px', fontWeight: 900, letterSpacing: '1px' }}>ORDER TOKEN</div>
          <div style={{ fontSize: is4Inch ? '32px' : '26px', fontWeight: 900, lineHeight: 1 }}>#{tokenNo}</div>
          <div style={{ fontSize: '8px', fontWeight: 700 }}>PLEASE WAIT FOR YOUR NUMBER</div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px dashed #000', paddingBottom: '4px', marginBottom: '4px' }}>
          <div>
            <div style={{ fontSize: is4Inch ? '14px' : '12px', fontWeight: 900, textTransform: 'uppercase' }}>{storeName}</div>
            {data.storePhone && <div style={{ fontSize: '8px' }}>{data.storePhone}</div>}
          </div>
          <div style={{ textAlign: 'right', fontSize: '9px' }}>
            <div>{time}</div>
            <div>{date}</div>
          </div>
        </div>

        {renderItemsTable(false)}
        {renderTotals()}
        {renderPayments()}
        {renderFooter()}
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════
  // TEMPLATE 10: CYBER CLEAN TECH
  // ══════════════════════════════════════════════════════════════════
  return (
    <div id={id} className={`kangavena-receipt-card ${className}`} style={containerStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #000', paddingBottom: '6px', marginBottom: '6px' }}>
        <div>
          <div style={{ fontSize: is4Inch ? '15px' : '13px', fontWeight: 900, textTransform: 'uppercase' }}>{storeName}</div>
          <div style={{ fontSize: '8px', fontWeight: 800 }}>POINT OF SALE TERMINAL</div>
        </div>
        <div style={{ border: '1px solid #000', padding: '2px 6px', borderRadius: '12px', fontSize: '8px', fontWeight: 900 }}>
          STATUS: PAID
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', borderBottom: '1px dotted #000', paddingBottom: '3px', marginBottom: '4px' }}>
        <span>ID: {data.id}</span>
        <span>CASHIER: {data.cashierName || 'POS'}</span>
        <span>{time}</span>
      </div>

      {renderItemsTable(true)}
      {renderTotals()}
      {renderPayments()}

      <div style={{ textAlign: 'center', borderTop: '1px dotted #000', paddingTop: '4px', marginTop: '6px', fontSize: '8px', letterSpacing: '2px' }}>
        • • • • • • • • • • • • • • • •
      </div>
      {renderFooter()}
    </div>
  );
};

export default ReceiptTemplateRenderer;
